import React from 'react';
import styled from 'styled-components';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend,
  ArcElement,
  PointElement,
  LineElement
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const ChartContainer = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  padding: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const ChartTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
`;

const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing[4]};
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const pieOptions = {
  responsive: true,
  maintainAspectRatio: true,
  plugins: {
    legend: {
      position: 'bottom',
    },
    title: {
      display: false,
    },
  },
};

const barOptions = {
  responsive: true,
  maintainAspectRatio: true,
  plugins: {
    legend: {
      position: 'top',
    },
    title: {
      display: false,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
    },
  },
};

const lineOptions = {
  responsive: true,
  maintainAspectRatio: true,
  plugins: {
    legend: {
      position: 'top',
    },
    title: {
      display: false,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
    },
  },
};

export const TimeDistributionByCustomer = ({ data = [] }) => {
  const chartData = {
    labels: data.map(item => item.name),
    datasets: [
      {
        label: 'Hours',
        data: data.map(item => item.hours),
        backgroundColor: [
          'rgba(54, 162, 235, 0.7)',
          'rgba(75, 192, 192, 0.7)',
          'rgba(255, 205, 86, 0.7)',
          'rgba(153, 102, 255, 0.7)',
          'rgba(255, 159, 64, 0.7)',
        ],
        borderColor: [
          'rgb(54, 162, 235)',
          'rgb(75, 192, 192)',
          'rgb(255, 205, 86)',
          'rgb(153, 102, 255)',
          'rgb(255, 159, 64)',
        ],
        borderWidth: 1,
      },
    ],
  };

  return (
    <ChartContainer>
      <ChartTitle>Time Distribution by Customer</ChartTitle>
      <Pie data={chartData} options={pieOptions} />
    </ChartContainer>
  );
};

export const TimeDistributionByEmployee = ({ data = [] }) => {
  const chartData = {
    labels: data.map(item => item.name),
    datasets: [
      {
        label: 'Hours',
        data: data.map(item => item.hours),
        backgroundColor: [
          'rgba(54, 162, 235, 0.7)',
          'rgba(75, 192, 192, 0.7)',
          'rgba(255, 205, 86, 0.7)',
          'rgba(153, 102, 255, 0.7)',
          'rgba(255, 159, 64, 0.7)',
          'rgba(201, 203, 207, 0.7)',
        ],
        borderColor: [
          'rgb(54, 162, 235)',
          'rgb(75, 192, 192)',
          'rgb(255, 205, 86)',
          'rgb(153, 102, 255)',
          'rgb(255, 159, 64)',
          'rgb(201, 203, 207)',
        ],
        borderWidth: 1,
      },
    ],
  };

  return (
    <ChartContainer>
      <ChartTitle>Time Distribution by Employee</ChartTitle>
      <Pie data={chartData} options={pieOptions} />
    </ChartContainer>
  );
};

export const TimeDistributionByStatus = ({ data = [] }) => {
  const chartData = {
    labels: data.map(item => item.status),
    datasets: [
      {
        label: 'Hours',
        data: data.map(item => item.hours),
        backgroundColor: [
          'rgba(75, 192, 192, 0.7)',
          'rgba(255, 205, 86, 0.7)',
          'rgba(255, 99, 132, 0.7)',
        ],
        borderColor: [
          'rgb(75, 192, 192)',
          'rgb(255, 205, 86)',
          'rgb(255, 99, 132)',
        ],
        borderWidth: 1,
      },
    ],
  };

  return (
    <ChartContainer>
      <ChartTitle>Time Distribution by Status</ChartTitle>
      <Pie data={chartData} options={pieOptions} />
    </ChartContainer>
  );
};

export const MonthlyHoursChart = ({ data = [] }) => {
  const chartData = {
    labels: data.map(item => item.month),
    datasets: [
      {
        label: 'Hours',
        data: data.map(item => item.hours),
        backgroundColor: 'rgba(54, 162, 235, 0.5)',
        borderColor: 'rgb(54, 162, 235)',
        borderWidth: 1,
      },
    ],
  };

  return (
    <ChartContainer>
      <ChartTitle>Monthly Hours (Year-to-Date)</ChartTitle>
      <Bar data={chartData} options={barOptions} />
    </ChartContainer>
  );
};

export const MonthlyTrendChart = ({ data = [] }) => {
  const chartData = {
    labels: data.map(item => item.month),
    datasets: [
      {
        label: 'Hours',
        data: data.map(item => item.hours),
        fill: false,
        backgroundColor: 'rgb(75, 192, 192)',
        borderColor: 'rgba(75, 192, 192, 0.8)',
        tension: 0.1
      },
    ],
  };

  return (
    <ChartContainer>
      <ChartTitle>Hours Trend (Year-to-Date)</ChartTitle>
      <Line data={chartData} options={lineOptions} />
    </ChartContainer>
  );
};

const ReportCharts = ({ distribution }) => {
  if (!distribution) {
    return <div>Loading charts...</div>;
  }

  return (
    <div>
      <ChartGrid>
        <TimeDistributionByCustomer data={distribution.byCustomer} />
        <TimeDistributionByEmployee data={distribution.byEmployee} />
      </ChartGrid>
      
      <ChartGrid>
        <TimeDistributionByStatus data={distribution.byStatus} />
        <MonthlyHoursChart data={distribution.byMonth} />
      </ChartGrid>

      <MonthlyTrendChart data={distribution.byMonth} />
    </div>
  );
};

export default ReportCharts;